// Form Protection Utilities
// Provides rate limiting, input sanitization, and validation

const RATE_LIMIT_STORAGE_KEY = 'form_submission_times';
const MAX_SUBMISSIONS_PER_HOUR = 3;
const RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000; // 1 hour

// Rate limiting: Check if user can submit (max 3 per hour)
export function checkRateLimit() {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
    const submissionTimes = stored ? JSON.parse(stored) : [];
    
    const now = Date.now();
    // Remove submissions older than 1 hour
    const recentSubmissions = submissionTimes.filter(
      time => now - time < RATE_LIMIT_WINDOW_MS
    );
    
    if (recentSubmissions.length >= MAX_SUBMISSIONS_PER_HOUR) {
      const oldestSubmission = recentSubmissions[0];
      const timeUntilNext = RATE_LIMIT_WINDOW_MS - (now - oldestSubmission);
      const minutesUntilNext = Math.ceil(timeUntilNext / (60 * 1000));
      
      return {
        allowed: false,
        message: `Too many submissions. Please wait ${minutesUntilNext} minute${minutesUntilNext !== 1 ? 's' : ''} before trying again.`
      };
    }
    
    return { allowed: true };
  } catch (error) {
    console.error('Rate limit check failed:', error);
    // Fail open - allow submission if we can't check
    return { allowed: true };
  }
}

// Record a submission for rate limiting
export function recordSubmission() {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
    const submissionTimes = stored ? JSON.parse(stored) : [];
    
    const now = Date.now();
    // Remove old submissions and add new one
    const recentSubmissions = submissionTimes.filter(
      time => now - time < RATE_LIMIT_WINDOW_MS
    );
    recentSubmissions.push(now);
    
    localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(recentSubmissions));
  } catch (error) {
    console.error('Failed to record submission:', error);
  }
}

// Sanitize input to prevent XSS and limit length
export function sanitizeInput(input, maxLength = 5000) {
  if (typeof input !== 'string') {
    return String(input || '').trim();
  }
  
  // Trim whitespace
  let sanitized = input.trim();
  
  // Remove HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, '');
  
  // Remove script tags and event handlers
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  
  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return sanitized;
}

// Validate email format
export function validateEmail(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  // Basic email regex (RFC 5322 simplified)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

// Validate Telegram username format
export function validateTelegram(username) {
  if (!username || typeof username !== 'string') {
    return false;
  }
  
  // Telegram usernames: @username or username (alphanumeric and underscores, 5-32 chars)
  const telegramRegex = /^@?[a-zA-Z0-9_]{5,32}$/;
  return telegramRegex.test(username.trim());
}

// Validate X (Twitter) username format
export function validateXUsername(username) {
  if (!username || typeof username !== 'string') {
    return false;
  }
  
  // X usernames: @username or username (alphanumeric and underscores, 1-15 chars)
  const xRegex = /^@?[a-zA-Z0-9_]{1,15}$/;
  return xRegex.test(username.trim());
}

// Validate contact based on type
export function validateContact(contact, contactType) {
  if (!contact || typeof contact !== 'string') {
    return { valid: false, message: 'Contact information is required' };
  }
  
  const trimmed = contact.trim();
  
  switch (contactType) {
    case 'Email':
      if (!validateEmail(trimmed)) {
        return { valid: false, message: 'Please enter a valid email address' };
      }
      break;
    case 'Telegram':
      if (!validateTelegram(trimmed)) {
        return { valid: false, message: 'Please enter a valid Telegram username (e.g., @username)' };
      }
      break;
    case 'X':
      if (!validateXUsername(trimmed)) {
        return { valid: false, message: 'Please enter a valid X (Twitter) username (e.g., @username)' };
      }
      break;
    default:
      return { valid: false, message: 'Invalid contact type' };
  }
  
  return { valid: true };
}

// Sanitize form data object
export function sanitizeFormData(formData) {
  return {
    name: sanitizeInput(formData.name || '', 100),
    contactType: formData.contactType || 'Email',
    contact: sanitizeInput(formData.contact || '', 200),
    company: sanitizeInput(formData.company || '', 200),
    subject: sanitizeInput(formData.subject || '', 200),
    message: sanitizeInput(formData.message || '', 5000)
  };
}

